export interface CodingField {
    name: string;
    offset: number;
    mask: number;
    values?: Record<string, number>;
    recommend?: string;
}

export type ParsedCoding = Record<string, number | string>;

function getShiftAmount(mask: number): number {
    let shift = 0;
    while (((mask >> shift) & 1) === 0) {
        shift++;
    }
    return shift;
}

// Byte 0 of the coding block is a check value over the remaining bytes: the XOR of
// bytes 1..47, plus one. The module's 2E 30 00 handler computes
// (c[0] - 1) ^ c[1] ^ ... ^ c[47] and refuses the write (fault 0xC0002) unless that
// comes out zero. Trailing unused bytes are zero so they don't affect the result.
export const computeCodingChecksum = (rawData: Uint8Array): number => {
    let x = 0;
    for (let i = 1; i < rawData.length; i++) {
        x ^= rawData[i];
    }
    return (x + 1) & 0xFF;
}

export const isCodingChecksumValid = (rawData: Uint8Array): boolean => {
    return rawData.length > 1 && rawData[0] === computeCodingChecksum(rawData);
}

// Returns a copy of the coding block with byte 0 set to match the rest of the data.
export const fixCodingChecksum = (rawData: Uint8Array): Uint8Array => {
    const newData = new Uint8Array(rawData);
    if (newData.length > 1) {
        newData[0] = computeCodingChecksum(newData);
    }
    return newData;
}

// Formats a block in NCS Expert netto data (.trc) style, 16 bytes per line:
//   B 003000<offset>,<length>,<byte>,<byte>,...
export const makeNettoData = (data: Uint8Array): string => {
    let result = '';

    let idx = 0;

    const lineLength = 16;

    while (idx < data.length) {
        result += 'B 003000' + idx.toString(16).toUpperCase().padStart(2, '0') + ',' + lineLength.toString(16).toUpperCase().padStart(4, '0');

        for (let i = 0; i < lineLength; i++) {
            result += ',' + data[idx].toString(16).toUpperCase().padStart(2, '0');

            idx++;
            if (idx === data.length) {
                break;
            }
        }

        result += '\n';
    }

    return result;
};

// Parses netto data in the format written by makeNettoData. Lines that aren't data
// blocks are ignored. The low byte of each block's address is its offset into the
// coding data. Throws if the blocks don't cover a contiguous range starting at 0.
export const parseNettoData = (text: string): Uint8Array => {
    const bytes: number[] = [];
    const covered: boolean[] = [];

    for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!/^B\s/i.test(line)) continue;

        const parts = line.substring(1).split(',').map(p => p.trim());
        if (parts.length < 2 || !parts.every(p => /^[0-9A-F]+$/i.test(p))) {
            throw new Error(`Malformed data line: ${line}`);
        }

        const offset = parseInt(parts[0], 16) & 0xFF;
        const data = parts.slice(2).map(p => parseInt(p, 16));
        if (data.some(b => b > 0xFF)) {
            throw new Error(`Byte value out of range: ${line}`);
        }

        data.forEach((b, i) => {
            bytes[offset + i] = b;
            covered[offset + i] = true;
        });
    }

    if (bytes.length === 0) {
        throw new Error('No coding data found in file');
    }

    for (let i = 0; i < bytes.length; i++) {
        if (!covered[i]) {
            throw new Error(`Missing coding data at offset 0x${i.toString(16).toUpperCase()}`);
        }
    }

    return new Uint8Array(bytes);
};

export const parseCoding = (rawData: Uint8Array, fields: CodingField[]): ParsedCoding => {
    const parsed: ParsedCoding = {};

    for (const field of fields) {
        const { name, offset, mask, values } = field;

        if (offset >= rawData.length) {
            console.warn(`offset index ${offset} out of bounds for field ${name}`);
            continue;
        }

        const shift = getShiftAmount(mask);
        const value = (rawData[offset] & mask) >> shift;

        // Try to map value to a known name
        const mappedValue = values ? Object.keys(values).find(key => values[key] === value) : undefined;
        parsed[name] = mappedValue ?? value;
    }

    return parsed;
}

export const applyCoding = (rawData: Uint8Array, changes: ParsedCoding, fields: CodingField[]): Uint8Array => {
    const newData = new Uint8Array(rawData);

    for (const field of fields) {
        if (!(field.name in changes)) continue;

        let newValue = changes[field.name];
        // Try to map the string back to bits
        if (typeof newValue === "string" && field.values) {
            newValue = field.values[newValue];
        }
        if (typeof newValue !== "number") continue;

        const shift = getShiftAmount(field.mask);
        newData[field.offset] = (newData[field.offset] & ~field.mask) | ((newValue << shift) & field.mask);
    }

    // Any change to the data invalidates the check value in byte 0
    if (newData.length > 1) {
        newData[0] = computeCodingChecksum(newData);
    }

    return newData;
}

const value1active: Record<string, number> = {
    'Inactive': 0,
    'Active': 1,
};

const value1inactive: Record<string, number> = {
    'Inactive': 1,
    'Active': 0,
};

const mk60e5_common: CodingField[] = [
    {
        name: 'Gearbox type',
        offset: 2,
        mask: 0xC0,
        values: {
            'Manual': 0,
            'Automatic': 1,
            'DCT': 2,
        },
        recommend: 'Manual',
    },
    {
        name: 'Brake pad wear compensation (BBV)',
        offset: 3,
        mask: 0x01,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Engine drag control (Pre-MSR)',
        offset: 3,
        mask: 0x02,
        values: value1inactive,
        recommend: 'Inactive',
    },
    {
        name: 'Brake fan control (Luefter?)',
        offset: 3,
        mask: 0x04,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Tire puncture warning (RPA)',
        offset: 3,
        mask: 0x08,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Cornering brake control (CBC)',
        offset: 3,
        mask: 0x10,
        values: value1inactive,
        recommend: 'Inactive',
    },
    {
        name: 'SDR_CTC',
        offset: 3,
        mask: 0x20,
        values: value1inactive,
        recommend: 'Inactive',
    },
    {
        name: 'Pre understeer control',
        offset: 3,
        mask: 0x80,
        values: value1inactive,
        recommend: 'Inactive',
    },
    {
        name: 'DDS Plus',
        offset: 0x0A,
        mask: 0x10,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Rotation direction rear axle',
        offset: 0x0A,
        mask: 0x40,
        values: {
            'Direction sensing': 0,
            'Direction ignoring': 1,
        },
        recommend: 'Direction sensing',
    },
    {
        name: 'Lane change logic (LCL)',
        offset: 0x0A,
        mask: 0x80,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Soft stop assist (SSA)',
        offset: 0x0D,
        mask: 0x08,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Ready alert brake',
        offset: 0x0D,
        mask: 0x10,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Brake disc drying (TB_BDD)',
        offset: 0x0D,
        mask: 0x20,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Hill start assist',
        offset: 0x0D,
        mask: 0x40,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Brake fade compensation',
        offset: 0x0D,
        mask: 0x80,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Yaw moment compensation',
        offset: 0x0E,
        mask: 0x0F,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Trailer stabilization control',
        offset: 0x0E,
        mask: 0x10,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'GMB',
        offset: 0x0E,
        mask: 0x20,
        values: value1active,
    },
    {
        name: 'Tire puncture warning (RPA) threshold',
        offset: 0x0E,
        mask: 0xC0,
    },
    {
        name: 'Hill start assist level',
        offset: 0x0F,
        mask: 0xFF,
    },
    {
        name: 'BBV Brake pad wear compensation front axle',
        offset: 0x10,
        mask: 0x0F,
    },
    {
        name: 'BBV Brake pad wear compensation rear axle',
        offset: 0x10,
        mask: 0xF0,
    },
];

const mk60e5_NonM_only: CodingField[] = [
    {
        name: 'Model variant',
        offset: 1,
        mask: 0x1F,
        values: {
            'Custom ESM': 0,
        }
    },
    {
        name: 'Diff lock',
        offset: 1,
        mask: 0x20,
    },
    {
        name: 'WLC',
        offset: 1,
        mask: 0x40,
    },
    {
        name: 'Cooling shaft (???)',
        offset: 1,
        mask: 0x80,
    },
    {
        name: 'Pressure model rear axle',
        offset: 2,
        mask: 0x07,
        values: {
            '40mm piston': 0,
            '40mm, low take-up': 1,
            '42mm piston': 2,
            '44mm piston': 3,
            '44mm piston alt': 4,
        }
    },
    {
        name: 'Pressure model front axle',
        offset: 2,
        mask: 0x38,
        values: {
            '54mm piston, stiff': 0,
            '54mm piston, soft': 1,
            '57mm piston, stiff': 2,
            '57mm piston, soft': 3,
            '57mm piston, softest': 4,
            '57mm piston, low take up': 5,
        }
    },
    {
        name: 'US Vehicle',
        offset: 3,
        mask: 0x40,
        values: value1active,
    },
    {
        name: 'Active front steering (AFS)',
        offset: 0x0A,
        mask: 0x20,
        values: value1active,
        recommend: 'Inactive',
    },
    {
        name: 'Rotor effective friction radius front',
        offset: 17,
        mask: 0x0f,
        values: {
            "115mm": 8,
            "118mm": 9,
            "121mm": 10,
            "124mm": 11,
            "127mm": 12,
            "130mm": 13,
            "133mm": 14,
            "136mm": 15,
            "139mm": 0,
            "142mm": 1,
            "145mm": 2,
            "148mm": 3,
            "151mm": 4,
            "154mm": 5,
            "157mm": 6,
            "160mm": 7
        }
    },
    {
        name: 'Rotor effective friction radius rear',
        offset: 17,
        mask: 0xf0,
        values: {
            "115mm": 8,
            "118mm": 9,
            "121mm": 10,
            "124mm": 11,
            "127mm": 12,
            "130mm": 13,
            "133mm": 14,
            "136mm": 15,
            "139mm": 0,
            "142mm": 1,
            "145mm": 2,
            "148mm": 3,
            "151mm": 4,
            "154mm": 5,
            "157mm": 6,
            "160mm": 7
        }
    }
];

const mk60e5_M3_only: CodingField[] = [
    {
        name: 'Model variant',
        offset: 1,
        mask: 0x1F,
        values: {
            'Custom ESM': 1,
            'M3 Sedan': 0,
            'M3 Coupe': 3,
            'M3 Convertible': 4,
            'M3 Competition Sedan': 5,
            'M3 Competition Coupe': 8,
            'M3 Competition Convertible': 9,
            'M3 GTS Coupe': 10,
            'M3 GTS Sedan': 11,
        },
    },
    {
        name: 'Engine type',
        offset: 1,
        mask: 0xE0,
    },
    {
        name: 'Pressure model rear axle',
        offset: 2,
        mask: 0x07,
        values: {
            'M3': 0,
            'M3 GTS': 1,
        },
    },
    {
        name: 'Pressure model front axle',
        offset: 2,
        mask: 0x38,
        values: {
            'M3': 0,
            'M3 GTS': 1,
        },
    },
    {
        name: 'Engine power reduction (prevent brake overheating)',
        offset: 3,
        mask: 0x40,
        values: value1active,
    },
    {
        name: 'USA M Dynamic mode',
        offset: 0x0A,
        mask: 0x20,
        values: value1active,
        recommend: 'Inactive',
    },
];

// Merge the common parts with the variant-specific stuff, then sort by offset
const mk60e5_M3 = [...mk60e5_common, ...mk60e5_M3_only].sort((a, b) => a.offset - b.offset);
const mk60e5_NonM = [...mk60e5_common, ...mk60e5_NonM_only].sort((a, b) => a.offset - b.offset);

const validateFields = (fields: CodingField[]): boolean => {
    const seen: Record<number, number> = {};

    for (const field of fields) {
        if (field.offset in seen) {
            if (seen[field.offset] & field.mask) {
                console.error(`Field overlap detected at offset ${field.offset} for field ${field.name}`);
                return false;
            }
        }
        seen[field.offset] = (seen[field.offset] || 0) | field.mask;
    }

    return true;
}

if (!validateFields(mk60e5_M3)) throw new Error('Field overlap validation failed for mk60e5_M3');
if (!validateFields(mk60e5_NonM)) throw new Error('Field overlap validation failed for mk60e5_NonM');

export const parsemk60_m3 = (rawData: Uint8Array): { parsed: ParsedCoding, fields: CodingField[], checksumValid: boolean } => {
    return {parsed: parseCoding(rawData, mk60e5_M3), fields: mk60e5_M3, checksumValid: isCodingChecksumValid(rawData)};
};

export const parsemk60_nonm = (rawData: Uint8Array): { parsed: ParsedCoding, fields: CodingField[], checksumValid: boolean } => {
    return {parsed: parseCoding(rawData, mk60e5_NonM), fields: mk60e5_NonM, checksumValid: isCodingChecksumValid(rawData)};
};
